import {
  ComponentPropsWithoutRef,
  CSSProperties,
  ReactElement,
  forwardRef,
  isValidElement,
} from 'react';
import MuiTooltip, { TooltipProps } from '@mui/material/Tooltip';
import {
  common,
  custom2,
  error,
  grey,
  normalText,
  other,
  secondary,
} from 'assets/colors/colors';
import { truncateSpanStyle } from './style';

type Variant = 'primary' | 'secondary' | 'error' | 'light';

export interface ITooltip extends Omit<TooltipProps, 'children'> {
  children?: ReactElement;
  variant?: Variant;
  boxShadow?: boolean;
  maxWidth?: string;
  /** When set, renders truncated text and shows tooltip only when text exceeds this length */
  maxLength?: number;
  truncateStyle?: CSSProperties;
  truncateProps?: ComponentPropsWithoutRef<'span'> & {
    'data-testid'?: string;
  };
}

const variantTextColor: Record<Variant, string> = {
  primary: common.white,
  secondary: common.white,
  error: error.main,
  light: normalText.main,
};

const variantBackgroundColor: Record<Variant, string> = {
  primary: grey.g700,
  secondary: secondary.main,
  error: custom2.main,
  light: common.white,
};

const getComponentsProps = (
  variant: Variant,
  boxShadow: boolean,
  maxWidth: string,
) => ({
  tooltip: {
    sx: {
      color: variantTextColor[variant],
      background: variantBackgroundColor[variant],
      boxShadow: boxShadow
        ? `8px 8px 8px 8px ${other.customBoxShadow}`
        : 'none',
      maxWidth,
    },
  },
  arrow: {
    sx: {
      color: variantBackgroundColor[variant],
    },
  },
});

export const Tooltip = forwardRef<unknown, ITooltip>(
  (
    {
      children,
      arrow,
      title,
      variant = 'primary',
      boxShadow = false,
      maxWidth = '300px',
      maxLength,
      truncateStyle,
      truncateProps,
      ...rest
    },
    ref,
  ) => {
    const componentsProps = getComponentsProps(variant, boxShadow, maxWidth);

    if (maxLength != null) {
      const text = String(title ?? '');
      const content = title ?? '';

      if (text.length <= maxLength - 1) {
        return (
          <span style={truncateStyle} {...truncateProps}>
            {content}
          </span>
        );
      }

      const truncatedLabel =
        text.length > maxLength
          ? `${text.substring(0, maxLength)}...`
          : content;

      return (
        <MuiTooltip
          title={title}
          arrow={arrow}
          PopperProps={{
            sx: {
              zIndex: '10000001 !important',
            },
          }}
          componentsProps={componentsProps}
          {...rest}
        >
          <span
            style={{ ...truncateSpanStyle, ...truncateStyle }}
            {...truncateProps}
          >
            {truncatedLabel}
          </span>
        </MuiTooltip>
      );
    }

    if (!isValidElement(children)) {
      return null;
    }

    return (
      <MuiTooltip
        ref={ref}
        title={title}
        arrow={arrow}
        PopperProps={{
          sx: {
            zIndex: '10000001 !important',
          },
        }}
        componentsProps={componentsProps}
        {...rest}
      >
        {children}
      </MuiTooltip>
    );
  },
);
